import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteCard from '@/components/base/SiteCard';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { useFavorites } from '@/hooks/useFavorites';
import { getCategoryBySlug, getRelatedSites, getSiteById } from '@/utils/nav';

function activityLevel(hot: number) {
  if (hot >= 9500) {
    return { label: '极高', className: 'bg-accent-100 text-accent-700' };
  }
  if (hot >= 8500) {
    return { label: '较高', className: 'bg-secondary-100 text-secondary-700' };
  }
  return { label: '中等', className: 'bg-background-200 text-foreground-700' };
}

export default function Detail() {
  const { id = '' } = useParams();
  const site = getSiteById(id);
  const category = site ? getCategoryBySlug(site.category) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo({
    title: site ? `${site.name} - 蓝导航` : '网站未找到 - 蓝导航',
    description: site ? site.description : '未找到该网站信息，请返回首页重新选择。',
    keywords: site
      ? `蓝导航,${site.name},${site.subCategory},${site.tags.join(',')}`
      : '蓝导航,网站导航',
  });

  if (!site || !category) {
    return (
      <EmptyState
        icon="ri-link-unlink-m"
        title="未找到该网站"
        description="你访问的网站详情不存在或已下架，请返回首页浏览其他导航入口。"
        action={{ to: '/', label: '返回首页' }}
      />
    );
  }

  const related = getRelatedSites(site, 3);
  const level = activityLevel(site.hot);
  const favorited = isFavorite(site.id);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    toggle(site.id);
    showToast(favorited ? '已取消收藏' : '已加入收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('分享链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制浏览器地址');
    }
  };

  return (
    <div className="w-full">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="cursor-pointer hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={`/category/${category.slug}`}
          className="cursor-pointer hover:text-primary-600"
        >
          {category.label}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{site.name}</span>
      </nav>

      <section className="relative mt-4 overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 px-6 py-8 md:px-10 md:py-10">
        <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center">
          <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-background-50 font-heading text-3xl font-extrabold text-primary-600">
            {site.initial}
          </span>

          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl dark:text-foreground-950">
              {site.name}
            </h1>
            <p className="mt-1 truncate text-sm text-background-100 dark:text-foreground-200">
              {site.url}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50 ring-1 ring-background-50/25 dark:text-foreground-950">
                {category.label}
              </span>
              <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50 ring-1 ring-background-50/25 dark:text-foreground-950">
                {site.subCategory}
              </span>
              <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50 ring-1 ring-background-50/25 dark:text-foreground-950">
                {site.region}
              </span>
            </div>
          </div>

          <div className="flex gap-2 sm:flex-col">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                favorited
                  ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-50 text-primary-700 hover:bg-background-100'
              }`}
            >
              <i className={favorited ? 'ri-star-fill' : 'ri-star-line'}></i>
              {favorited ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-background-50/15 px-4 py-2 text-sm font-medium text-background-50 ring-1 ring-background-50/30 transition-colors hover:bg-background-50/25 dark:text-foreground-950"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>
        </div>
      </section>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              网站介绍
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {site.name} 是「{category.label}」领域中专注于{site.subCategory}
              方向的网站，{site.description}蓝导航收录该站点，方便你在需要时快速访问，免去反复搜索与记忆网址的繁琐。
            </p>

            <h3 className="mt-6 font-heading text-base font-bold text-foreground-950">
              核心服务
            </h3>
            <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {site.tags.map((tag) => (
                <li
                  key={tag}
                  className="flex items-start gap-2.5 rounded-md bg-background-100 px-3.5 py-3"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-check-line"></i>
                  </span>
                  <span className="text-sm text-foreground-700">
                    提供与「{tag}」相关的功能与服务
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              网站信息
            </h2>
            <dl className="mt-4 flex flex-col gap-3.5 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">所属分类</dt>
                <dd className="font-medium text-foreground-900">{category.label}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">子分类</dt>
                <dd className="font-medium text-foreground-900">{site.subCategory}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">地区</dt>
                <dd className="font-medium text-foreground-900">{site.region}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">活跃程度</dt>
                <dd
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${level.className}`}
                >
                  {level.label}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">热度指数</dt>
                <dd className="flex items-center gap-1 font-medium text-foreground-900">
                  <i className="ri-fire-line text-accent-500"></i>
                  {site.hot}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">收录时间</dt>
                <dd className="font-medium text-foreground-900">{site.addedAt}</dd>
              </div>
            </dl>

            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              访问官网
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <p className="mt-2 text-center text-xs text-foreground-400">
              将在新标签页打开外部网站
            </p>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <SiteCard key={item.id} site={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-md bg-foreground-950 px-4 py-3 text-sm text-background-50">
          <i className="ri-checkbox-circle-line text-secondary-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}